import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppConstants } from '@wz-constants';
import { AddressService } from '@wz-services';
import {
  applyFormConfig,
  getControlWithValueChanges,
} from '@wz-utilities/form.utilities';
import {
  FormGroupBaseComponent,
  getFormGroupBaseDirective,
} from 'libs/modules/tunnelsante-forms/components/form-group-base.component';
import { AutomobileRiskModels } from 'models/automobile-risk.models';
import { FormsModels } from 'models/forms.model';
import { QuotationModels } from 'models/quotation.models';
import { finalize } from 'rxjs/operators';
import { FormConfigModels } from '../../../../../../models/form-config.models';

@Component({
  selector: 'wz-infos-form',
  templateUrl: './infos-form.component.html',
  styleUrls: ['./infos-form.component.scss'],
  standalone: false,
  providers: [getFormGroupBaseDirective(InfosFormComponent)],
})
export class InfosFormComponent
  extends FormGroupBaseComponent<AutomobileRiskModels.Infos>
  implements OnChanges
{
  @Output() usageChange = new EventEmitter<QuotationModels.UsageEnum>();
  @Input() config!: FormConfigModels.UsageConfig;

  private _zipcode!: string;
  get zipCode() {
    return this._zipcode;
  }
  @Input()
  set zipCode(zip: string) {
    if (
      (this._zipcode || this.formGroup.status == 'INVALID') &&
      zip &&
      zip.length == 5
    )
      this.formGroup.patchValue({
        codeInseePerso: { codePostal: zip },
        codeInseePro: { codePostal: zip },
      });

    this._zipcode = zip;
  }

  public isFetchingCities = false;
  public cityList!: FormsModels.SelectListItem[];

  public isFetchingProCities = false;
  public cityProList!: FormsModels.SelectListItem[];

  private lastProCodeInseeGroup!: AutomobileRiskModels.CodeInseeGroup | any;

  public forTunnelApp = false;

  constructor(
    private fb: FormBuilder,
    private addressService: AddressService,
  ) {
    super();

    this.formGroup = this.createInfoFormGroup();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      applyFormConfig(this.formGroup, this.config);
    }
  }

  private getCodeInseeGroup(
    isFetching: (b: boolean) => void,
    list: (l: FormsModels.SelectListItem[]) => void,
    defaultValue?: AutomobileRiskModels.CodeInseeGroup,
    onSelect?: (gr: AutomobileRiskModels.CodeInseeGroup) => void,
  ): FormGroup {
    const codeInseeCtrl = getControlWithValueChanges(
      this.fb,
      { value: defaultValue ? defaultValue.codeInsee : null, disabled: true },
      [],
      (_codeInseeCtrl) => {
        if (onSelect) {
          onSelect({
            codeInsee: codeInseeCtrl.value,
            codePostal: codePostalCtrl.value,
          });
        }
      },
      this.destroy$,
    );

    const codePostalCtrl = getControlWithValueChanges(
      this.fb,
      defaultValue ? defaultValue.codePostal : null,
      [Validators.required],
      (zipCtrl) => {
        if (zipCtrl.valid) {
          isFetching(true);
          codeInseeCtrl.enable();
          this.addressService
            .getCities(zipCtrl.value)
            .pipe(
              // delayAtLeast(AppConstants.Delay.Fast),
              finalize(() => isFetching(false)),
            )
            .subscribe((res: any) => {
              list(res);
              if (res.length > 0) {
                if (
                  codeInseeCtrl.value &&
                  res.findIndex((c: any) => c.value === codeInseeCtrl.value) !==
                    -1
                ) {
                  return;
                } else {
                  codeInseeCtrl.setValue(res[0].value);
                }
              }
            });
        } else {
          codeInseeCtrl.disable();
          list([]);
        }
      },
      this.destroy$,
      AppConstants.Delay.Medium,
    );

    return this.fb.group({
      codePostal: codePostalCtrl,
      codeInsee: codeInseeCtrl,
    });
  }

  private createInfoFormGroup() {
    const usageCtrl: any = this.fb.control('', [Validators.required]);
    const group = this.fb.group({
      titulaireCarteGrise: this.fb.control([Validators.required]),
      usage: usageCtrl,
      garage: this.fb.control('', [Validators.required]),
      financement: this.fb.control('', [Validators.required]),
      nombreKmAnnuel: this.fb.control('', [Validators.required]),
      vehiculeDejaAssure: this.fb.control('', [Validators.required]),
      codeInseePerso: this.getCodeInseeGroup(
        (b) => (this.isFetchingCities = b),
        (list) => (this.cityList = list),
      ),
      codeInseePro: this.getCodeInseeGroup(
        (b) => (this.isFetchingProCities = b),
        (list) => (this.cityProList = list),
        this.lastProCodeInseeGroup,
        (gr) => (this.lastProCodeInseeGroup = gr),
      ),
    });

    usageCtrl.valueChanges.subscribe((val: any) => {
      this.usageChange.next(val);
      const codePostal: any =
        this.formGroup.controls['codeInseePro'].get('codePostal');
      if (usageCtrl.value !== QuotationModels.UsageEnum.Prive) {
        codePostal.setValidators(Validators.required);
      } else {
        codePostal.clearValidators();
      }
      codePostal.updateValueAndValidity();
    });

    return group;
  }

  override onPatchValue(val: AutomobileRiskModels.Infos) {
    if (val) {
      this.lastProCodeInseeGroup = val.codeInseePro;
    }
  }

  customizeForTunnelApp() {
    (this.formGroup.controls as any).nombreKmAnnuel.setValue(
      QuotationModels.NombreKilometresAnnuelEnum.PlusDe35000,
    );
    this.forTunnelApp = true;
  }
}
